/**
 * DateInput — shared types, Zod prop schema, styling constants and the date
 * helpers.
 *
 * A fieldset of three numeric boxes, Month, Day and Year, that together report
 * one value: the three parts as typed plus an ISO date (`yyyy-MM-dd`) when the
 * parts make a real calendar date. There is no calendar picker. Every date
 * calculation goes through Luxon in a fixed zone (ADR-096); the global `Date`
 * is never used.
 *
 * Source: Figma file hk9NaJrKRwkoGmmZ5rxbsE, page v7, "Web/Date of birth input"
 * (node 47:5424). Date of birth is one use; the birthday autofill hint is the
 * `autoComplete` prop. The v7 sheet was drawn from this package's code rather
 * than the UI Design System library, so its variable names stand until design
 * adds them to the library.
 */

import { DateTime } from 'luxon';
import { z } from 'zod';
import { colors } from '../../elements/colors';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { states } from '../../elements/states';
import { typography } from '../../elements/typography';
import type { TypeStyle } from '../../elements/typography';
import { TEXT_INPUT_BORDER_RADIUS, TEXT_INPUT_HEIGHT } from '../TextInput/TextInput';

/** The three boxes, in the order they render. */
export const DATE_INPUT_PART_ORDER = ['month', 'day', 'year'] as const;
export type DateInputPart = (typeof DATE_INPUT_PART_ORDER)[number];

/** How many digits each box accepts. */
export const DATE_INPUT_PART_MAX_DIGITS: Readonly<Record<DateInputPart, number>> = {
  month: 2,
  day: 2,
  year: 4,
};

const partCopySchema = z.object({
  month: z.string().optional(),
  day: z.string().optional(),
  year: z.string().optional(),
});

export const dateInputPartsSchema = z.object({
  month: z.string(),
  day: z.string(),
  year: z.string(),
});

export type DateInputParts = z.infer<typeof dateInputPartsSchema>;

/** Catalog-facing schema: data props only; callbacks are platform props. */
export const dateInputPropsSchema = z.object({
  /** The fieldset's visible legend, e.g. "Date of birth". */
  legend: z.string().optional(),
  /** Small label above each box, e.g. Month / Day / Year. */
  partLabels: partCopySchema.optional(),
  /** Placeholder in each empty box, e.g. MM / DD / YYYY. */
  partPlaceholders: partCopySchema.optional(),
  helperText: z.string().optional(),
  errorText: z.string().optional(),
  disabled: z.boolean().default(false),
  /** `bday` marks the boxes as a birthday for browser autofill. */
  autoComplete: z.enum(['bday', 'off']).default('off'),
  /** The three parts as digit strings, e.g. { month: "03", day: "14", year: "1986" }. */
  value: dateInputPartsSchema.optional(),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

export type DateInputProps = z.infer<typeof dateInputPropsSchema>;

/** The one value every edit reports. */
export interface DateInputValue extends DateInputParts {
  /** `yyyy-MM-dd` when the parts make a real calendar date, else null. */
  isoDate: string | null;
}

export const DATE_INPUT_EMPTY_PARTS: DateInputParts = { month: '', day: '', year: '' };

// ---------------------------------------------------------------------------
// Date helpers (Luxon, fixed zone)
// ---------------------------------------------------------------------------

// A calendar date has no time of day, so one fixed zone keeps every check the
// same wherever the browser is.
const DATE_INPUT_ZONE = 'UTC';

/** Keeps the digits of `text`, up to the box's limit. */
export function dateInputPartDigits(part: DateInputPart, text: string): string {
  return text.replace(/\D/g, '').slice(0, DATE_INPUT_PART_MAX_DIGITS[part]);
}

/** The ISO date the parts make, or null when they are not a real date. */
export function dateInputIsoDate(parts: DateInputParts): string | null {
  if (!/^\d{1,2}$/.test(parts.month) || !/^\d{1,2}$/.test(parts.day)) return null;
  if (!/^\d{4}$/.test(parts.year)) return null;
  const date = DateTime.fromObject(
    { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) },
    { zone: DATE_INPUT_ZONE },
  );
  return date.isValid ? date.toISODate() : null;
}

/** The parts plus their ISO date: the value an edit reports. */
export function dateInputValueOf(parts: DateInputParts): DateInputValue {
  return { ...parts, isoDate: dateInputIsoDate(parts) };
}

function partsOfDate(date: DateTime): DateInputParts {
  return {
    month: String(date.month).padStart(2, '0'),
    day: String(date.day).padStart(2, '0'),
    year: String(date.year).padStart(4, '0'),
  };
}

/**
 * Turns a known calendar date (`yyyy-MM-dd`) into parts, for a screen
 * pre-filling a date. Anything else, a timestamp included, gives empty parts:
 * reading a timestamp in a fixed zone could move it to the next or previous
 * day.
 */
export function dateInputPartsFromIso(iso: string): DateInputParts {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return DATE_INPUT_EMPTY_PARTS;
  const date = DateTime.fromISO(iso, { zone: DATE_INPUT_ZONE });
  return date.isValid ? partsOfDate(date) : DATE_INPUT_EMPTY_PARTS;
}

/** Whole-date shapes a paste may hold, US order first. */
const PASTED_DATE_FORMATS = ['M/d/yyyy', 'M-d-yyyy', 'M.d.yyyy', 'yyyy-M-d', 'MMddyyyy'];

// The same shapes as plain patterns, so a date that is not real (02/30/1990)
// still fills the boxes as typed rather than being dropped.
const PASTED_DATE_SHAPES: ReadonlyArray<{ pattern: RegExp; order: 'mdy' | 'ymd' }> = [
  { pattern: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/, order: 'mdy' },
  { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: 'ymd' },
  { pattern: /^(\d{2})(\d{2})(\d{4})$/, order: 'mdy' },
];

/**
 * The parts held in pasted text when it is a whole date (03/14/1986,
 * 3-14-1986, 1986-03-14, 03141986), else null so the paste is treated as
 * ordinary typing into one box.
 */
export function dateInputPartsFromPaste(text: string): DateInputParts | null {
  const trimmed = text.trim();
  for (const format of PASTED_DATE_FORMATS) {
    const date = DateTime.fromFormat(trimmed, format, { zone: DATE_INPUT_ZONE });
    if (date.isValid) return partsOfDate(date);
  }
  for (const { pattern, order } of PASTED_DATE_SHAPES) {
    const match = pattern.exec(trimmed);
    if (!match) continue;
    const [first = '', second = '', third = ''] = match.slice(1);
    const [month, day, year] = order === 'mdy' ? [first, second, third] : [second, third, first];
    return { month: month.padStart(2, '0'), day: day.padStart(2, '0'), year };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Styling constants — all derived from design-system elements
// ---------------------------------------------------------------------------

export const DATE_INPUT_BOX_HEIGHT = TEXT_INPUT_HEIGHT;
export const DATE_INPUT_BOX_RADIUS = TEXT_INPUT_BORDER_RADIUS;
export const DATE_INPUT_BORDER_WIDTH = states.borderWidth.default;
export const DATE_INPUT_BOX_HORIZONTAL_PADDING = spacing.m;
export const DATE_INPUT_YEAR_WIDTH = sizing.dateInput.yearWidth;
export const DATE_INPUT_BOX_GAP = spacing.s;
export const DATE_INPUT_LABEL_VALUE_GAP = spacing.xxs;
/** Gap between the legend, the boxes and the helper or error. */
export const DATE_INPUT_SECTION_GAP = spacing.xs;

// v7 draws the legend at 14/20 bold, which has no type style; b3Bold keeps the
// size and weight at a 22 line height until one is added.
export const DATE_INPUT_LEGEND_TYPOGRAPHY: TypeStyle = typography.b3Bold;
export const DATE_INPUT_PART_LABEL_TYPOGRAPHY: TypeStyle = typography.c2Regular;
export const DATE_INPUT_VALUE_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const DATE_INPUT_HELPER_TYPOGRAPHY: TypeStyle = typography.c1Medium;
export const DATE_INPUT_PLACEHOLDER_COLOR = colors.text.secondary;
export const DATE_INPUT_DISABLED_PLACEHOLDER_COLOR = colors.text.tertiary;

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export interface DateInputBoxState {
  disabled: boolean;
  hasError: boolean;
  /** This box has focus. */
  focused: boolean;
  /** The mouse is over this box. */
  hovered: boolean;
}

export interface DateInputBoxColors {
  background: string;
  border: string;
  label: string;
  value: string;
}

/** One box's colours: hover and focus darken only that box, as v7 draws it. */
export function dateInputBoxColorsFor({
  disabled,
  hasError,
  focused,
  hovered,
}: DateInputBoxState): DateInputBoxColors {
  // v7's `stroke/alert` error border; `button.critical` holds the same colour
  // as `stroke.alert`.
  const border = disabled
    ? colors.stroke.divider
    : hasError
      ? colors.button.critical
      : focused || hovered
        ? colors.stroke.primary
        : colors.stroke.input;
  return {
    background: disabled ? colors.background.secondary : colors.background.container,
    border,
    label: disabled ? colors.text.tertiary : colors.text.secondary,
    value: disabled ? colors.text.tertiary : colors.text.primary,
  };
}

export interface DateInputTextColors {
  legend: string;
  helper: string;
}

export function dateInputTextColorsFor(disabled: boolean): DateInputTextColors {
  return {
    legend: disabled ? colors.text.tertiary : colors.text.primary,
    helper: disabled ? colors.text.tertiary : colors.text.secondary,
  };
}
